export type PublishSandboxItemStatus = 'published' | 'failed' | 'skipped';

export interface PublishResultBase {
  name: string;
  status: PublishSandboxItemStatus;
  published_at: string | null;
  error: string | null;
}

export interface PublishResponse<T extends PublishResultBase> {
  total: number;
  published: number;
  failed: number;
  results: T[];
}

export interface SnapshotPublishResult extends PublishResultBase {
  snapshot_id: string;
}

export interface GiftPublishResult extends PublishResultBase {
  gift_id: string;
  code: string;
}

export interface ItemPublishResult extends PublishResultBase {
  item_id: string;
  type: string;
}

export interface SeasonPassPublishResult extends PublishResultBase {
  season_pass_id: string;
  code: string;
}

export interface PublishSandboxResponse {
  sandbox_id: string;
  sandbox_code: string;
  success: boolean;
  sandbox_streamed: boolean;
  items: PublishResponse<ItemPublishResult>;
  snapshots: PublishResponse<SnapshotPublishResult>;
  gifts: PublishResponse<GiftPublishResult>;
  // Results contains failed passes only (backend trims successes to save memory).
  season_passes: PublishResponse<SeasonPassPublishResult>;
}

export type PublishOutcome = 'complete' | 'partial' | 'failed';

export const publishSections = (response: PublishSandboxResponse) =>
  [
    ['Items', response.items],
    ['Snapshots', response.snapshots],
    ['Gifts', response.gifts],
    ['Season Passes', response.season_passes],
  ] as const;

export const publishTotals = (response: PublishSandboxResponse) =>
  publishSections(response).reduce(
    (totals, [, { published, failed }]) => ({
      published: totals.published + published,
      failed: totals.failed + failed,
    }),
    { published: 0, failed: 0 },
  );

export const publishOutcome = (
  response: PublishSandboxResponse,
): PublishOutcome => {
  const { published, failed } = publishTotals(response);
  if (response.success && failed === 0) return 'complete';
  if (published === 0) return 'failed';
  return 'partial';
};
